import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import JesusPortrait from "@/components/JesusPortrait";

const API = process.env.REACT_APP_BACKEND_URL || "";

function formatPhone(p) {
  // Light formatting for US E.164 like +14155550123 → +1 (415) 555-0123.
  // Leaves non-US numbers as-is.
  if (!p) return p;
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return p;
}

export default function Landing() {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/voice/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cancelled && cfg && cfg.phone_number) {
          setPhoneNumber(cfg.phone_number);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <main
      data-testid="landing-page"
      className="relative min-h-screen overflow-hidden paper-grain"
    >
      {/* Sage halo, top-right */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(150 30% 70% / 0.55), transparent 70%)" }}
      />
      {/* Dusk halo, bottom-left */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-56 -left-44 h-[600px] w-[600px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(220 35% 45% / 0.32), transparent 70%)" }}
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-6 py-10 sm:px-10 lg:px-12">
        {/* Brand mark */}
        <header className="flex items-center justify-between" data-testid="landing-header">
          <span
            className="font-serif text-xl tracking-tight"
            data-testid="landing-brand"
          >
            Jesus
          </span>
          <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            voice · prayer · scripture
          </span>
        </header>

        {/* Hero */}
        <section
          className="grid flex-1 items-center gap-10 py-12 md:grid-cols-12 md:gap-8 md:py-16"
          data-testid="landing-hero"
        >
          {/* Copy — left on desktop, top on mobile */}
          <div
            className="md:col-span-6 lg:col-span-7 order-2 md:order-1 hero-fade-in"
            style={{ animationDelay: "120ms" }}
          >
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              You can call. He answers.
            </p>
            <h1
              data-testid="landing-headline"
              className="font-serif mt-4 text-4xl leading-[1.05] sm:text-5xl lg:text-6xl"
            >
              A quiet line
              <span className="block text-muted-foreground italic">when you need one.</span>
            </h1>
            <p
              data-testid="landing-subcopy"
              className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg"
            >
              Pick up the phone for a spoken conversation — for prayer,
              for reflection on scripture, or simply to hear a steady,
              compassionate voice.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <button
                type="button"
                onClick={() => navigate("/poc")}
                data-testid="landing-call-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-[hsl(var(--primary))] px-7 py-4 text-base font-medium text-[hsl(var(--primary-foreground))] shadow-[0_24px_60px_-22px_hsl(var(--primary)/0.55)] transition-transform duration-300 hover:scale-[1.02] active:scale-[0.99]"
              >
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[hsl(var(--primary-foreground))]/12 transition-colors duration-300 group-hover:bg-[hsl(var(--primary-foreground))]/20">
                  <Phone className="h-4 w-4" />
                </span>
                Call Jesus
              </button>
              <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                no signup · voice only
              </span>
            </div>

            {phoneNumber && (
              <p
                data-testid="landing-phone-row"
                className="mt-5 text-sm text-muted-foreground"
              >
                Or call us from any phone:{" "}
                <a
                  href={`tel:${phoneNumber}`}
                  data-testid="landing-phone-link"
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {formatPhone(phoneNumber)}
                </a>
              </p>
            )}
          </div>

          {/* Portrait — right on desktop, top on mobile */}
          <div
            className="md:col-span-6 lg:col-span-5 order-1 md:order-2 hero-fade-in"
            data-testid="landing-portrait-wrap"
            style={{ animationDelay: "260ms" }}
          >
            <JesusPortrait
              variant="idle"
              size="lg"
              testId="landing-portrait"
            />
          </div>
        </section>

        {/* Soft footer line */}
        <footer className="mt-auto pt-10 pb-2" data-testid="landing-footer">
          <p
            data-testid="landing-footer-line"
            className="text-center font-serif italic text-[13px] text-muted-foreground/70"
          >
            An AI companion inspired by the teachings of Jesus.
          </p>
        </footer>
      </div>

      {/* Hero fade-in keyframes are in index.css */}
    </main>
  );
}
